/**
 * Digi-Cute-Theme — Pastell-Rosarot-Thema.
 * Verwendet dasselbe Zwei-Pass-createTheme-Muster wie Light/Dark-Themes.
 */

import {createTheme} from '@mui/material';

import {cute} from './customColors';
import {digiCutePalette} from './digiCutePalette';
import {
  sharedTypography,
  sharedBreakpoints,
  sharedTypographyOverrides,
} from './sharedThemeConfig';
import bgDigiCute from '../assets/bg_digicute.svg';

const cuteFont = '"Comic Sans MS", "Comic Sans", cursive, sans-serif';

/**
 * Erster Durchlauf: Palette + Typografie + Breakpoints
 */
const baseTheme = createTheme({
  palette: digiCutePalette,
  typography: {
    ...sharedTypography,
    fontFamily: cuteFont,
  },
  breakpoints: sharedBreakpoints,
});

export const digiCuteTheme = createTheme(baseTheme, {
  components: {
    MuiPaper: {
      defaultProps: {variant: 'outlined'},
      styleOverrides: {
        root: {
          borderColor: cute.border,
          backgroundColor: cute.whitePaperMid,
        },
      },
    },
    MuiMenu: {defaultProps: {slotProps: {paper: {variant: 'elevation'}}}},
    MuiButton: {
      styleOverrides: {
        contained: {
          borderRadius: 20,
          textTransform: 'none' as const,
          fontWeight: 600,
          '&:hover': {
            boxShadow: cute.shadow,
            backgroundColor: cute.pinkDark,
            color: cute.white,
          },
        },
        outlined: {
          borderRadius: 20,
          borderColor: cute.pink,
          '&:hover': {
            borderColor: cute.pinkDark,
            backgroundColor: cute.pinkLight,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {'&:focus': {backgroundColor: 'transparent'}},
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: baseTheme.palette.secondary.main,
          },
          '&.MuiTableRow-hover:hover': {
            backgroundColor: baseTheme.palette.secondary.light,
          },
          '&.Mui-selected.MuiTableRow-hover:hover': {
            backgroundColor: baseTheme.palette.secondary.dark,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          borderColor: cute.border,
          backgroundColor: cute.whitePaperMid,
          '&.selected': {
            backgroundColor: baseTheme.palette.secondary.main,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiTreeItem: {
      styleOverrides: {
        content: {
          '&:hover': {
            backgroundColor: baseTheme.palette.secondary.light,
          },
          '&[data-selected]:hover, &[data-selected][data-focused]:hover': {
            backgroundColor: baseTheme.palette.secondary.dark,
          },

          '&[data-selected], &[data-selected][data-focused]': {
            backgroundColor: baseTheme.palette.secondary.main,
          },
          '&[data-focused]': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiTypography: sharedTypographyOverrides,
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': {
          fontFamily: `${cuteFont} !important`,
        },
        body: {
          backgroundColor: cute.pinkLight,
          backgroundImage: `url(${bgDigiCute})`,
          backgroundSize: 'cover',
          backgroundAttachment: 'fixed',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: cute.lavenderLight,
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            backgroundColor: cute.pink,
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover':
            {
              backgroundColor: cute.pinkMid,
            },
        },
      },
    },
  },
});
